import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/** Marks a request as already-retried once, so a 401 on the retry doesn't loop forever. */
const IS_RETRY = new HttpContextToken<boolean>(() => false);

const UNAUTHENTICATED_AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh'];

function isUnauthenticatedAuthEndpoint(url: string): boolean {
  return UNAUTHENTICATED_AUTH_ENDPOINTS.some((endpoint) =>
    url.includes(endpoint),
  );
}

/**
 * Attaches the stored access token to every request except login/refresh, and keeps that token
 * alive two ways:
 *
 * 1. **Before the request**, when the token's own `exp` says it is already spent. Reacting to a 401
 *    alone was not enough: the catalogue routes use `optionalAuth`, which answers a stale token with
 *    the *public* view rather than a 401, so an expired CMS session silently degraded to "published
 *    only" instead of repairing itself. See `AuthService.shouldRefreshBeforeRequest`.
 * 2. **After a 401**, for everything the first rule cannot see coming — a token revoked server-side,
 *    a clock further out than the skew allows, or one this build cannot parse.
 *
 * Either way it is a single refresh: concurrent callers share one in-flight call through
 * `AuthService.refresh()`, and a request is only ever retried once.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const skipAuthHeader = isUnauthenticatedAuthEndpoint(req.url);

  /** The refresh itself failed: the session is over, wherever we were called from. */
  const endSession = (error: unknown): Observable<never> => {
    authService.clearSession();
    void router.navigate(['/login']);
    return throwError(() => error);
  };

  const withToken = (token: string | null): HttpRequest<unknown> =>
    req.clone({
      setHeaders:
        token && !skipAuthHeader ? { Authorization: `Bearer ${token}` } : {},
    });

  /** Send with `token`, and repair-then-retry once if the server rejects it anyway. */
  const send = (token: string | null) => {
    const authorizedReq = withToken(token);
    return next(authorizedReq).pipe(
      catchError((error: unknown) => {
        const shouldAttemptRefresh =
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !skipAuthHeader &&
          !req.context.get(IS_RETRY);

        if (!shouldAttemptRefresh) {
          return throwError(() => error);
        }

        // `catchError` sits *before* `switchMap` so it only ever sees a failed refresh. Downstream
        // of it, it would also swallow the retried request's own failures — a 500 or a 404 on the
        // retry would then sign the author out for a fault that has nothing to do with their session.
        return authService.refresh().pipe(
          catchError(endSession),
          switchMap((tokens) =>
            next(
              authorizedReq.clone({
                setHeaders: { Authorization: `Bearer ${tokens.accessToken}` },
                context: authorizedReq.context.set(IS_RETRY, true),
              }),
            ).pipe(
              catchError((retryError: unknown) =>
                // A fresh token refused as well: the session really is finished. Any other status is
                // the endpoint's problem, and is passed to the caller untouched.
                retryError instanceof HttpErrorResponse && retryError.status === 401
                  ? endSession(retryError)
                  : throwError(() => retryError),
              ),
            ),
          ),
        );
      }),
    );
  };

  if (!skipAuthHeader && authService.shouldRefreshBeforeRequest()) {
    // Same ordering rule: only the refresh's own failure ends the session.
    return authService.refresh().pipe(
      catchError(endSession),
      switchMap((tokens) => send(tokens.accessToken)),
    );
  }

  return send(authService.accessToken);
};
